import { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  ButtonBase,
  Divider,
  Paper,
  SwipeableDrawer,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import AppsRounded from '@mui/icons-material/AppsRounded';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import BusinessRounded from '@mui/icons-material/BusinessRounded';
import PersonRounded from '@mui/icons-material/PersonRounded';
import PhotoCameraRounded from '@mui/icons-material/PhotoCameraRounded';
import InstallMobileRounded from '@mui/icons-material/InstallMobileRounded';
import CloudDoneRounded from '@mui/icons-material/CloudDoneRounded';
import CloudOffRounded from '@mui/icons-material/CloudOffRounded';
import CloudSyncRounded from '@mui/icons-material/CloudSyncRounded';
import PointOfSaleRounded from '@mui/icons-material/PointOfSaleRounded';
import AccountBalanceWalletRounded from '@mui/icons-material/AccountBalanceWalletRounded';
import RequestQuoteRounded from '@mui/icons-material/RequestQuoteRounded';
import HandshakeRounded from '@mui/icons-material/HandshakeRounded';
import Inventory2Rounded from '@mui/icons-material/Inventory2Rounded';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppThemeContext, useAuthContext, useDrawerContext } from '../../contexts';
import { THEME_OPTIONS } from '../../themes';
import { ModuleIcon } from '../app-shell';
import { IconeAlertaCertificado, useStatusCertificado } from '../certificado/AvisoCertificado';
import { useOffline } from '../../offline/useOffline';
import { PainelSincronizacao } from '../../offline/PainelSincronizacao';
import { formaInstalacao, inscreverInstalacao, instalarApp } from '../../pwa/instalacao';
import { DialogInstalarIos } from '../../pwa/DialogInstalarIos';

// Navegação no celular, no jeito dos apps de banco (mesmo padrão do Finanças
// Pessoais — ver AppMobile.tsx de lá): sem menu lateral — barra fixa embaixo
// com Início + os módulos principais, um botão "+" no meio pra criar rápido
// (venda, conta a receber/pagar...) e "Mais" abrindo uma gaveta de baixo pra
// cima com o resto. Diferente do Finanças, os módulos do SIAC têm submenus
// (drawerItemOptions): tocar num módulo abre uma gaveta com as telas dele.
// Tudo respeita as permissões do usuário (drawerOptions/drawerItemOptions já
// vêm filtrados de routes/index.tsx).

// Na barra de baixo, nesta ordem, os 2 primeiros módulos que o usuário puder
// abrir (além de Início); o resto vai pro "Mais".
const PRIORIDADE = ['/vendas', '/financeiro', '/estoque', '/cadastros', '/fiscal', '/servicos'];
export const ALTURA_BARRA_MOBILE = 64;

// Atalhos do "+": só aparecem se a tela de destino estiver liberada pro
// usuário (pelo path da listagem em drawerItemOptions).
const ATALHOS_NOVO = [
  { listagem: '/venda', destino: '/venda/novo', rotulo: 'Nova venda', detalhe: 'Pedido, orçamento', icone: <PointOfSaleRounded />, cor: 'primary' as const },
  { listagem: '/contasreceber', destino: '/contasreceber/novo', rotulo: 'Conta a receber', detalhe: 'Lançamento a receber', icone: <AccountBalanceWalletRounded />, cor: 'success' as const },
  { listagem: '/contaspagar', destino: '/contaspagar/novo', rotulo: 'Conta a pagar', detalhe: 'Boleto, fornecedor', icone: <RequestQuoteRounded />, cor: 'error' as const },
  { listagem: '/parceiros', destino: '/parceiros/novo', rotulo: 'Parceiro', detalhe: 'Cliente ou fornecedor', icone: <HandshakeRounded />, cor: 'info' as const },
  { listagem: '/produto', destino: '/produto/novo', rotulo: 'Produto', detalhe: 'Cadastro de produto', icone: <Inventory2Rounded />, cor: 'warning' as const },
];

const getBaseRoute = (path: string) => path.split('/').filter(Boolean)[0] || '';

interface IAppMobileProps {
  children: React.ReactNode;
  nomeUsuario: string;
  loginUsuario: string;
  fotoPerfil: string;
  onAbrirPerfil: () => void;
  onAbrirFoto: () => void;
}

export const AppMobile: React.FC<IAppMobileProps> = ({ children, nomeUsuario, loginUsuario, fotoPerfil, onAbrirPerfil, onAbrirFoto }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { drawerOptions, drawerItemOptions } = useDrawerContext();
  const { logout } = useAuthContext();
  const { themeName, setThemeName } = useAppThemeContext();
  const offline = useOffline();
  // Certificado digital vencido/com problema: ponto vermelho no "Mais" (o
  // alerta em si fica ao lado do avatar, dentro da gaveta).
  const certificado = useStatusCertificado();
  const alertaCertificado = !!certificado && ['vencido', 'erro'].includes(certificado.situacao);

  const [moduloAberto, setModuloAberto] = useState<string | null>(null);
  const [maisAberto, setMaisAberto] = useState(false);
  const [novoAberto, setNovoAberto] = useState(false);
  const [sincAberta, setSincAberta] = useState(false);
  const [instalarIos, setInstalarIos] = useState(false);
  const [formaInstalar, setFormaInstalar] = useState(formaInstalacao);
  useEffect(() => inscreverInstalacao(() => setFormaInstalar(formaInstalacao())), []);

  // Teclado aberto (foco num campo de digitação): esconde a barra de baixo —
  // no iPhone o teclado não encolhe a página, e a barra fixa ficava "pulando"
  // por cima/atrás dele ao rolar. Volta assim que o campo perde o foco.
  const [tecladoAberto, setTecladoAberto] = useState(false);
  useEffect(() => {
    const ehCampoDeTexto = (alvo: EventTarget | null) => {
      const el = alvo as HTMLElement | null;
      if (!el) return false;
      if (el.isContentEditable || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return true;
      if (el.tagName !== 'INPUT') return false;
      const tipo = (el as HTMLInputElement).type;
      return !['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'range', 'color'].includes(tipo);
    };
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const aoFocar = (evento: FocusEvent) => {
      if (!ehCampoDeTexto(evento.target)) return;
      if (timeout) clearTimeout(timeout);
      setTecladoAberto(true);
    };
    // Pequena espera: pular de um campo pro outro dispara focusout e logo
    // depois focusin — sem isso a barra piscaria entre os dois.
    const aoDesfocar = () => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => setTecladoAberto(ehCampoDeTexto(document.activeElement)), 120);
    };
    document.addEventListener('focusin', aoFocar);
    document.addEventListener('focusout', aoDesfocar);
    return () => {
      if (timeout) clearTimeout(timeout);
      document.removeEventListener('focusin', aoFocar);
      document.removeEventListener('focusout', aoDesfocar);
    };
  }, []);

  // Fecha qualquer gaveta ao trocar de tela (ex.: voltar do navegador).
  useEffect(() => {
    setModuloAberto(null);
    setMaisAberto(false);
    setNovoAberto(false);
  }, [location.pathname]);

  const atual = getBaseRoute(location.pathname);
  const subitensDe = (modulo: string) => drawerItemOptions.filter((item) => item.parent === modulo);
  // Módulo "ativo" = a tela atual é ele mesmo ou uma das telas dele.
  const moduloAtivo = (modulo: string) =>
    getBaseRoute(modulo) === atual || subitensDe(modulo).some((item) => getBaseRoute(item.path) === atual);

  const inicio = drawerOptions.find((d) => d.path === '/inicio');
  const modulos = useMemo(
    () => drawerOptions
      .filter((d) => d.path !== '/inicio')
      .sort((a, b) => {
        const ia = PRIORIDADE.indexOf(a.path);
        const ib = PRIORIDADE.indexOf(b.path);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      }),
    [drawerOptions],
  );
  const naBarra = modulos.slice(0, 2);
  const noMais = modulos.slice(2);
  const atalhos = ATALHOS_NOVO.filter((a) => drawerItemOptions.some((item) => item.path === a.listagem));
  const maisAtivo = noMais.some((d) => moduloAtivo(d.path)) || ['empresa'].includes(atual);

  const ir = (path: string) => {
    setModuloAberto(null);
    setMaisAberto(false);
    setNovoAberto(false);
    navigate(path);
    window.scrollTo({ top: 0 });
  };

  // Módulo sem submenu navega direto; com submenu abre a gaveta dele.
  const abrirModulo = (path: string) => {
    if (subitensDe(path).length === 0) { ir(path); return; }
    setMaisAberto(false);
    setModuloAberto(path);
  };

  const iniciais = nomeUsuario.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');
  const corAtiva = theme.palette.primary.main;
  const corInativa = theme.palette.text.secondary;
  const statusConexao = `${offline.sincronizacao.sincronizando ? 'Sincronizando' : offline.online ? 'Online' : 'Sem internet'}${offline.pendentes > 0 ? ` • ${offline.pendentes} pendente${offline.pendentes > 1 ? 's' : ''}` : ''}`;
  const corConexao = offline.comErro > 0 ? theme.palette.error.main : offline.online ? theme.palette.success.main : theme.palette.warning.main;

  const itemBarra = (chave: string, rotulo: string, icone: React.ReactNode, ativo: boolean, onClick: () => void) => (
    <ButtonBase
      key={chave}
      onClick={onClick}
      aria-label={rotulo}
      sx={{ flex: 1, minWidth: 0, height: '100%', flexDirection: 'column', gap: 0.25, color: ativo ? corAtiva : corInativa, borderRadius: '12px' }}
    >
      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 28, borderRadius: '14px',
          bgcolor: ativo ? alpha(corAtiva, 0.12) : 'transparent', transition: 'background-color 150ms ease',
        }}
      >
        {icone}
      </Box>
      <Typography noWrap sx={{ fontSize: '0.68rem', fontWeight: ativo ? 700 : 500, lineHeight: 1.1, maxWidth: '100%' }}>{rotulo}</Typography>
    </ButtonBase>
  );

  // Linha de lista das gavetas.
  const linha = (icone: React.ReactNode, rotulo: string, onClick: () => void, detalhe?: string, cor?: string) => (
    <ButtonBase
      onClick={onClick}
      sx={{ width: '100%', justifyContent: 'flex-start', gap: 1.75, px: 2.5, py: 1.5, textAlign: 'left', color: cor || 'text.primary' }}
    >
      <Box sx={{ display: 'flex', color: cor || 'text.secondary', '& svg': { fontSize: 22 } }}>{icone}</Box>
      <Box flex={1} minWidth={0}>
        <Typography fontWeight={600} fontSize="0.95rem">{rotulo}</Typography>
        {detalhe && <Typography variant="caption" color="text.secondary" component="div" noWrap>{detalhe}</Typography>}
      </Box>
      <ChevronRightRounded sx={{ color: 'text.disabled' }} />
    </ButtonBase>
  );

  // Grade de ícones (telas de um módulo, módulos no "Mais").
  const blocoGrade = (chave: string, icone: React.ReactNode, rotulo: string, ativo: boolean, onClick: () => void) => (
    <ButtonBase
      key={chave}
      onClick={onClick}
      sx={{
        flexDirection: 'column', gap: 0.75, py: 1.75, px: 0.5, borderRadius: '16px', minWidth: 0,
        bgcolor: ativo ? alpha(corAtiva, 0.12) : theme.palette.action.hover, color: ativo ? corAtiva : 'text.primary',
      }}
    >
      {icone}
      <Typography fontSize="0.78rem" fontWeight={600} textAlign="center" lineHeight={1.2} sx={{ wordBreak: 'break-word' }}>{rotulo}</Typography>
    </ButtonBase>
  );

  const gavetaSx = {
    '& .MuiDrawer-paper': {
      borderTopLeftRadius: 24, borderTopRightRadius: 24,
      pb: 'calc(12px + env(safe-area-inset-bottom))', maxHeight: '88vh',
    },
  };
  const puxador = <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: 'divider', mx: 'auto', mt: 1.25, mb: 1 }} />;
  const moduloDaGaveta = drawerOptions.find((d) => d.path === moduloAberto);

  return (
    <>
      <Box
        component="main"
        sx={{
          minHeight: '100dvh',
          bgcolor: 'background.default',
          // iPhone instalado: o conteúdo começa abaixo do relógio e termina acima da barra de baixo
          pt: 'env(safe-area-inset-top)',
          pb: `calc(${ALTURA_BARRA_MOBILE + 16}px + env(safe-area-inset-bottom))`,
          overflowX: 'hidden',
        }}
      >
        {!offline.online && (
          <Box
            onClick={() => setSincAberta(true)}
            sx={{ px: 2, py: 0.75, display: 'flex', alignItems: 'center', gap: 1, bgcolor: alpha(theme.palette.warning.main, 0.15), color: 'warning.dark', cursor: 'pointer' }}
          >
            <CloudOffRounded sx={{ fontSize: 18 }} />
            <Typography variant="caption" fontWeight={700}>
              Sem internet — trabalhando com os dados deste aparelho{offline.pendentes > 0 ? ` (${offline.pendentes} pendente${offline.pendentes > 1 ? 's' : ''})` : ''}
            </Typography>
          </Box>
        )}
        {children}
      </Box>

      <Paper
        elevation={0}
        component="nav"
        sx={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: theme.zIndex.appBar,
          // Camada própria (translate3d): no iPhone evita a barra "tremer" ou
          // se deslocar durante a rolagem. Com o teclado aberto, desliza pra
          // fora da tela (ver tecladoAberto).
          transform: tecladoAberto ? 'translate3d(0, calc(100% + 24px), 0)' : 'translate3d(0, 0, 0)',
          transition: 'transform 180ms ease',
          willChange: 'transform',
          touchAction: 'none',
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(15,23,42,0.92)' : 'rgba(255,255,255,0.94)',
          backdropFilter: 'saturate(180%) blur(16px)',
          pb: 'env(safe-area-inset-bottom)',
        }}
      >
        <Box sx={{ height: ALTURA_BARRA_MOBILE, display: 'flex', alignItems: 'center', px: 0.5 }}>
          {inicio && itemBarra(inicio.path, 'Início', <ModuleIcon name={inicio.icon} sx={{ fontSize: 22 }} />, atual === 'inicio', () => ir(inicio.path))}
          {naBarra.slice(0, 1).map((d) => itemBarra(d.path, d.label, <ModuleIcon name={d.icon} sx={{ fontSize: 22 }} />, moduloAtivo(d.path) || moduloAberto === d.path, () => abrirModulo(d.path)))}
          {atalhos.length > 0 && (
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <ButtonBase
                onClick={() => setNovoAberto(true)}
                aria-label="Criar"
                sx={{
                  width: 56, height: 56, borderRadius: '18px', mt: -3,
                  color: '#fff', background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                  boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.35)}`,
                }}
              >
                <AddRounded sx={{ fontSize: 32 }} />
              </ButtonBase>
            </Box>
          )}
          {naBarra.slice(1).map((d) => itemBarra(d.path, d.label, <ModuleIcon name={d.icon} sx={{ fontSize: 22 }} />, moduloAtivo(d.path) || moduloAberto === d.path, () => abrirModulo(d.path)))}
          {itemBarra('mais', 'Mais', (
            <Box sx={{ position: 'relative', display: 'flex' }}>
              <AppsRounded sx={{ fontSize: 22 }} />
              {alertaCertificado && (
                <Box sx={{ position: 'absolute', top: -2, right: -4, width: 9, height: 9, borderRadius: '50%', bgcolor: 'error.main', border: `2px solid ${theme.palette.background.paper}` }} />
              )}
            </Box>
          ), maisAtivo || maisAberto, () => setMaisAberto(true))}
        </Box>
      </Paper>

      {/* Telas de um módulo */}
      <SwipeableDrawer anchor="bottom" open={!!moduloAberto} onOpen={() => undefined} onClose={() => setModuloAberto(null)} sx={gavetaSx} disableSwipeToOpen>
        {puxador}
        <Typography fontWeight={800} fontSize="1.1rem" sx={{ px: 2.5, pb: 1.5 }}>{moduloDaGaveta?.label}</Typography>
        <Box display="grid" gridTemplateColumns="repeat(3, minmax(0, 1fr))" gap={1} sx={{ px: 2, pb: 1, overflowY: 'auto' }}>
          {moduloAberto && subitensDe(moduloAberto).map((item) => blocoGrade(
            item.path,
            <ModuleIcon name={item.icon} sx={{ fontSize: 26, color: getBaseRoute(item.path) === atual ? corAtiva : theme.palette.text.secondary }} />,
            item.label,
            getBaseRoute(item.path) === atual,
            () => ir(item.path),
          ))}
        </Box>
      </SwipeableDrawer>

      {/* "+": criar rápido */}
      <SwipeableDrawer anchor="bottom" open={novoAberto} onOpen={() => setNovoAberto(true)} onClose={() => setNovoAberto(false)} sx={gavetaSx} disableSwipeToOpen>
        {puxador}
        <Typography fontWeight={800} fontSize="1.1rem" sx={{ px: 2.5, pb: 1.5 }}>O que você quer criar?</Typography>
        <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap={1.5} sx={{ px: 2.5, pb: 1 }}>
          {atalhos.map((a) => {
            const cor = theme.palette[a.cor].main;
            return (
              <ButtonBase
                key={a.destino}
                onClick={() => ir(a.destino)}
                sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 1, p: 2, borderRadius: '16px', bgcolor: alpha(cor, 0.08), border: `1px solid ${alpha(cor, 0.2)}`, textAlign: 'left' }}
              >
                <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: cor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{a.icone}</Box>
                <Box minWidth={0}>
                  <Typography fontWeight={800}>{a.rotulo}</Typography>
                  <Typography variant="caption" color="text.secondary">{a.detalhe}</Typography>
                </Box>
              </ButtonBase>
            );
          })}
        </Box>
      </SwipeableDrawer>

      {/* "Mais" */}
      <SwipeableDrawer anchor="bottom" open={maisAberto} onOpen={() => setMaisAberto(true)} onClose={() => setMaisAberto(false)} sx={gavetaSx} disableSwipeToOpen>
        {puxador}
        <Box sx={{ overflowY: 'auto' }}>
          <Box display="flex" alignItems="center" gap={1.5} sx={{ px: 2.5, pb: 2 }}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <Avatar src={fotoPerfil} sx={{ width: 48, height: 48, bgcolor: 'primary.main', fontWeight: 700 }}>{iniciais}</Avatar>
              {/* Certificado digital vencido/com problema — clicar explica. */}
              <Box sx={{ position: 'absolute', top: -6, right: -10 }}>
                <IconeAlertaCertificado tamanho={18} />
              </Box>
            </Box>
            <Box minWidth={0}>
              <Typography fontWeight={800} noWrap>{nomeUsuario}</Typography>
              <Typography variant="body2" color="text.secondary" noWrap>{loginUsuario}</Typography>
            </Box>
          </Box>

          {noMais.length > 0 && (
            <Box display="grid" gridTemplateColumns="repeat(3, minmax(0, 1fr))" gap={1} sx={{ px: 2, pb: 2 }}>
              {noMais.map((d) => blocoGrade(
                d.path,
                <ModuleIcon name={d.icon} sx={{ fontSize: 26, color: moduloAtivo(d.path) ? corAtiva : theme.palette.text.secondary }} />,
                d.label,
                moduloAtivo(d.path),
                () => abrirModulo(d.path),
              ))}
            </Box>
          )}

          <Divider />
          {linha(<BusinessRounded />, 'Configurações da empresa', () => ir('/empresa'), 'Dados da empresa e parâmetros do sistema')}
          {linha(
            offline.sincronizacao.sincronizando ? <CloudSyncRounded /> : offline.online ? <CloudDoneRounded /> : <CloudOffRounded />,
            'Conexão e sincronização',
            () => { setMaisAberto(false); setSincAberta(true); },
            statusConexao,
            corConexao,
          )}
          {formaInstalar && linha(<InstallMobileRounded />, 'Instalar SIAC no aparelho', () => {
            setMaisAberto(false);
            if (formaInstalar === 'ios') setInstalarIos(true); else instalarApp();
          }, 'Ícone na tela de início e funciona sem internet')}
          {linha(<PersonRounded />, 'Meu perfil', () => { setMaisAberto(false); onAbrirPerfil(); })}
          {linha(<PhotoCameraRounded />, 'Foto de perfil', () => { setMaisAberto(false); onAbrirFoto(); })}

          <Box sx={{ px: 2.5, py: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>TEMA</Typography>
            <Box display="flex" gap={1} mt={1}>
              {/* "menu claro" só muda o menu lateral, que no celular não existe */}
              {THEME_OPTIONS.filter((opt) => opt.name !== 'lightWhiteMenu').map((opt) => (
                <ButtonBase
                  key={opt.name}
                  onClick={() => setThemeName(opt.name)}
                  sx={{
                    flex: 1, gap: 0.75, py: 1, px: 1, borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600,
                    border: `1.5px solid ${themeName === opt.name ? corAtiva : theme.palette.divider}`,
                  }}
                >
                  <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: opt.swatch, border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {themeName === opt.name && <CheckRounded sx={{ fontSize: 10, color: '#fff' }} />}
                  </Box>
                  <span>{opt.label.replace(/ \(.*\)/, '')}</span>
                </ButtonBase>
              ))}
            </Box>
          </Box>

          <Divider />
          {linha(<LogoutRounded />, 'Sair do sistema', () => { setMaisAberto(false); logout(); }, undefined, theme.palette.error.main)}
        </Box>
      </SwipeableDrawer>

      <PainelSincronizacao open={sincAberta} onClose={() => setSincAberta(false)} />
      <DialogInstalarIos open={instalarIos} onClose={() => setInstalarIos(false)} />
    </>
  );
};
